import * as THREE from 'three'
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js'
import type { LayeredRelief } from './layeredEngravingRelief'
import { createRectGeometry, pixelsToMeters } from '~/composables/export3DGeometry'
import type { LayeredEngravingSelection } from '~/shared/layeredEngraving'

/** Two felt sheets, with artwork-derived relief and matching edge profiles on the top. */
export function createLayeredMesh(width: number, height: number, layer: LayeredEngravingSelection, front: THREE.MeshStandardMaterial, relief?: LayeredRelief) {
  const halfDepth = layer.depth_mm / 2
  const bottom = createRectGeometry(width, height, halfDepth, false)
  const top = relief ? createReliefGeometry(width, height, halfDepth, relief) : createRectGeometry(width, height, halfDepth, false)
  if (!relief) top.translate(0, 0, halfDepth / 1000)
  // BoxGeometry material slots: right, left, top, bottom, front, back.
  bottom.clearGroups()
  bottom.addGroup(0, bottom.index!.count, 0)
  if (!relief) top.groups.forEach(group => { group.materialIndex = group.materialIndex === 4 ? 2 : 1 })
  const geometry = mergeGeometries([bottom, top], false)!
  geometry.clearGroups()
  geometry.addGroup(0, bottom.index!.count, 0)
  top.groups.forEach(group => geometry.addGroup(bottom.index!.count + group.start, group.count, group.materialIndex))
  bottom.dispose()
  top.dispose()
  const materials = [
    new THREE.MeshStandardMaterial({ color: layer.bottom_hex, roughness: 1 }),
    new THREE.MeshStandardMaterial({ color: layer.top_hex, roughness: 1 }),
    front,
  ]
  return new THREE.Mesh(geometry, materials)
}

function createReliefGeometry(width: number, height: number, halfDepth: number, relief: LayeredRelief) {
  const { size, coverage } = relief
  const w = pixelsToMeters(width), h = pixelsToMeters(height), base = halfDepth / 1000
  const surface = new THREE.PlaneGeometry(w, h, size - 1, size - 1)
  const positions = surface.getAttribute('position')
  for (let i = 0; i < positions.count; i++) positions.setZ(i, base * (1 + coverage[i]))
  surface.computeVertexNormals()
  surface.clearGroups()
  surface.addGroup(0, surface.index!.count, 2)
  const coords: number[] = [], uv: number[] = [], indices: number[] = []
  // Clockwise perimeter as seen from the front; walls have outward normals.
  const perimeter: number[] = []
  for (let x = 0; x < size; x++) perimeter.push(x)
  for (let y = 1; y < size; y++) perimeter.push(y * size + size - 1)
  for (let x = size - 2; x >= 0; x--) perimeter.push((size - 1) * size + x)
  for (let y = size - 2; y > 0; y--) perimeter.push(y * size)
  perimeter.forEach((a, i) => {
    const b = perimeter[(i + 1) % perimeter.length], start = coords.length / 3
    coords.push(positions.getX(a), positions.getY(a), base, positions.getX(b), positions.getY(b), base,
      positions.getX(a), positions.getY(a), positions.getZ(a), positions.getX(b), positions.getY(b), positions.getZ(b))
    uv.push(0,0,1,0,0,1,1,1)
    indices.push(start, start+2, start+1, start+1, start+2, start+3)
  })
  const walls = new THREE.BufferGeometry()
  walls.setAttribute('position', new THREE.Float32BufferAttribute(coords, 3))
  walls.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2))
  walls.setIndex(indices); walls.computeVertexNormals()
  const combined = mergeGeometries([surface, walls], false)!
  combined.clearGroups()
  combined.addGroup(0, surface.index!.count, 2)
  combined.addGroup(surface.index!.count, walls.index!.count, 1)
  surface.dispose(); walls.dispose()
  return combined
}
